import React, { useEffect, useState,useContext  } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Button,
  UncontrolledTooltip,
  Row,
  Col,
  Input,
  Badge,
} from "reactstrap";

import CommonTable from "components/CommonComponents/ReactTable/Table";
import CommonModal from "components/CommonComponents/CommonModal";
import {
  fixedWidthCustomStyle,
  DEFAULT_RECORDS_PER_PAGE,
} from "constants/CommonConstants";
import Select from "react-select";
import {
  getTraineeAssessment,
  getTraineeAssessmentArtifactFiles,
} from "action/trainee";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import { getActiveCourseNameList } from "action/batch";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";


function TraineeAssessment() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const decodedId = id ? urlDecodedId(id) : null;

  const [pageNum, setPageNum] = useState(1);
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [showAssessmentModal, setShowAssessmentModal] = useState({
    isOpen: false,
    assessment: null,
  });
  const [searchState, setSearchState] = useState({
    assessmentName: "",
    courseName: null,
    batchName: "",
    filterParams: [],
  });
  const [alternative, setAlternative] = useState({
    isLoading: true,
    showLoaderOnClick: false,
    error: "",
    noRecords: false,
  });
  const [artifacts, setArtifacts] = useState([]);
  const traineeAssessmentDataList = useSelector(
    (state) => state.trainees.traineeAssessment,
  );

  const courseNameListData = useSelector(
    (state) => state.batch.activeCourseNameList,
  );
  const { notify } = useContext(NotificationContext);

  useEffect(() => {
    getActiveCourseNameList(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (decodedId) {
      setAlternative((prev) => ({
        ...prev,
        isLoading: true,
      }));

      getTraineeAssessment(
        dispatch,
        decodedId,
        pageNum,
        searchState.filterParams,
      );
    }
  }, [dispatch, decodedId, pageNum, searchState.filterParams]);

  useEffect(() => {
    if (traineeAssessmentDataList?.code === 200) {
      setAlternative({
        isLoading: false,
        error: "",
        noRecords: traineeAssessmentDataList?.data?.assessments?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (
      traineeAssessmentDataList?.code ||
      traineeAssessmentDataList?.message
    ) {
      setAlternative({
        isLoading: false,
        error:
          traineeAssessmentDataList?.message ||
          "Failed to load trainee assessment list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [traineeAssessmentDataList]);

  const buildFilterParams = () => {
    const filters = [];

    if (searchState.assessmentName?.trim()) {
      filters.push({
        field: "AssessmentName",
        condition: "like",
        filterValues: [searchState.assessmentName.trim()],
      });
    }

    if (searchState.courseName?.value) {
      filters.push({
        field: "CourseID",
        condition: "eq",
        filterValues: [String(searchState.courseName.value)],
      });
    }

    if (searchState.batchName) {
      filters.push({
        field: "BatchName",
        condition: "like",
        filterValues: [String(searchState.batchName)],
      });
    }

    return {
      filters: filters.length > 0 ? JSON.stringify(filters) : [],
    };
  };

  const formattedTraineeAssessmentList =
    traineeAssessmentDataList?.data?.assessments?.map((item, index) => {
      return {
        ...item,
        ID: item?.id,
        assessmentName: item?.assessmentName || "",
        courseName: item?.courseName || "",
        courseType: item?.courseType || "",
        batchName: item?.batchName || "",
        artifactCount: item?.artifactCount || 0,
        srNo:
          (pageNum - 1) *
            (traineeAssessmentDataList?.data?.noOfRecordsPerPage ||
              DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    }) || [];

  const courseOptions =
    courseNameListData?.data?.map((course) => ({
      value: course.ID,
      label: course.courseName,
    })) || [];

  const handleCloseModal = () => {
    setShowAssessmentModal({
      isOpen: false,
      assessment: null,
    });
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setAlternative((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setSearchState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const clearSearch = () => {
    setAlternative((prev) => ({
      ...prev,
      isLoading: true,
    }));

    setSearchState({
      assessmentName: "",
      courseName: "",
      batchName: "",
      filterParams: [],
    });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleView = async (assessment) => {
    setShowAssessmentModal({
      isOpen: true,
      assessment,
    });

    setArtifacts([]);
    setSelectedArtifact(null);
    setPreviewLoading(true);

    try {
      const response = await getTraineeAssessmentArtifactFiles(
        decodedId,
        assessment.id,
      );

      const artifactFiles = response?.data?.artifactFiles || [];

      setArtifacts(artifactFiles);

      if (artifactFiles.length > 0) {
        setSelectedArtifact(artifactFiles[0]);
      }
    } catch (error) {
       notify("Access Denied", "danger");
    } finally {
      setPreviewLoading(false);
    }
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      centerAlign: true,
        minWidth:60,
      maxWidth:70,
    },
    {
      Header: "Assessment Name",
      accessor: "assessmentName",
        minWidth:180,
      maxWidth: 200,
      Cell: ({ value }) => <div className="text-wrap-cell">{value}</div>,
    },
    {
      Header: "Course Name & Type",
      accessor: "courseName",
      minWidth: 200,
      maxWidth: 250,
      Cell: ({ row }) => (
        <div className="d-flex flex-column">
          <div className="font-weight-medium text-wrap">{row.original.courseName}</div>
          <div className="text-muted ">({row.original.courseType})</div>
        </div>
      ),
    },
    {
      Header: "Batch Name",
      accessor: "batchName",
       minWidth: 200,
      maxWidth: 250,
      Cell: ({ value }) => <div className="text-wrap-cell">{value}</div>,
    },

    {
      Header: "Assessment Type",
      accessor: "assessmentType",
      minWidth: 150,
      maxWidth: 150,
      align: "center",
    },
    {
      Header: "Available Dates",
      centerAlign: true,
      minWidth: 200,
      maxWidth: 250,
      Cell: ({ row }) => {
        const { availableFrom, availableTo } = row.original;

        if (!availableFrom || !availableTo) return "—";

        return (
          <div className="text-wrapper">
            <div>{availableFrom}</div>
            <div className="font-weight-bold ml-2">to</div>
            <div>{availableTo}</div>
          </div>
        );
      },
    },
    {
      Header: "Duration",
      centerAlign: true,
      minWidth: 100,
      maxWidth: 100,
      Cell: ({ row }) => {
        const { durationMinutes } = row.original;

        if (!durationMinutes) return "—";

        return `${durationMinutes} min`;
      },
    },
    {
      Header: "Passing %",
      centerAlign: true,
      minWidth: 130,
      maxWidth: 130,
      Cell: ({ row }) => {
        const { passingPercentage } = row.original;

        if (!passingPercentage) return "—";

        return `${passingPercentage}%`;
      },
    },
    {
      Header: "Marks",
      centerAlign: true,
      minWidth: 100,
      maxWidth: 100,
      Cell: ({ row }) => {
        const { obtainedMarks, totalMarks } = row.original;

        if (!totalMarks) return "—";

        return `${obtainedMarks} / ${totalMarks}`;
      },
    },
     {
      Header: "Result",
      centerAlign: true,
      minWidth: 100,
      maxWidth: 100,
      Cell: ({ row }) => {
        const { result } = row.original;

        if (!result) return "—";

        return (
          <Badge className="rounded-pill" color={result === "PASSED" ? "success" : "danger"}>
            {result === "PASSED" ? "Passed" : "Failed"}
          </Badge>
        );
      },
    },
   
    {
      Header: "Attempt Date",
      centerAlign: true,
      minWidth: 200,
      maxWidth: 250,
      Cell: ({ row }) => {
        const { startedDate, submissionDate } = row.original;

        if (!startedDate || !submissionDate) return "—";

        return (
          <div className="text-wrapper">
            <div>{startedDate}</div>
            <div className="font-weight-bold ml-2">to</div>
            <div>{submissionDate}</div>
          </div>
        );
      },
    },
    {
      Header: "Status",
      centerAlign: true,
      minWidth: 100,
      maxWidth: 100,
      Cell: ({ row }) => {
        const { status } = row.original;

        if (!status) return <div className="text-center">—</div>;

        return (
          <Badge className="rounded-pill"
            color={
              status === "AVAILABLE"
                ? "success"
                : status === "SUBMITTED"
                  ? "info"
                  : status === "EXPIRED"
                    ? "danger"
                    : "secondary"
            }
          >
            {status}
          </Badge>
        );
      },
    },
    {
      Header: "Artifacts",
      accessor: "action",
      Cell: ({ row }) => {
        const { assessmentType, artifactCount } = row.original;

        if (assessmentType !== "OFFLINE") {
          return <div className="text-center">—</div>;
        }

        return (
          <>
            <Button
              id={`view-assessment-${row.original.id}`}
              className="btn-link d-flex align-items-center"
              onClick={() => handleView(row.original)}
            >
              <i className="fa fa-paperclip mr-1 table-action-primary" />
              <span className="ml-1">{artifactCount || 0}</span>
            </Button>

            <UncontrolledTooltip target={`view-assessment-${row.original.id}`}>
              View Artifact
            </UncontrolledTooltip>
          </>
        );
      },
    },
  ];

  const renderPreview = (artifact) => {
    if (!artifact) {
      return (
        <div className="d-flex justify-content-center align-items-center h-100">
          Select an artifact to preview
        </div>
      );
    }
    const fileUrl = artifact?.previewUrl || artifact?.filePath || "";

    const extension = fileUrl
      ?.split("?")[0]
      ?.split("#")[0]
      ?.split(".")
      .pop()
      ?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
      return (
        <img
          src={fileUrl}
          alt={artifact.fileName}
          className="preview-image"
          onLoad={() => setPreviewLoading(false)}
          onError={() => setPreviewLoading(false)}
        />
      );
    }

    if (["mp4", "webm", "ogg"].includes(extension)) {
      return (
        <video
          controls
          className="preview-video"
          onLoadedData={() => setPreviewLoading(false)}
          onError={() => setPreviewLoading(false)}
        >
          <source src={fileUrl} />
          Your browser does not support the video tag.
        </video>
      );
    }

    if (extension === "pdf") {
      return (
        <iframe
          src={fileUrl}
          title={artifact.fileName}
          className="preview-frame"
          onLoad={() => setPreviewLoading(false)}
        />
      );
    }

    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(extension)) {
      return (
        <iframe
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
            fileUrl,
          )}`}
          title={artifact.fileName}
          className="preview-frame"
          onLoad={() => setPreviewLoading(false)}
        />
      );
    }

    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        Preview not available
      </div>
    );
  };

  return (
    <Card className="mb-6">
      <CardBody className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
          <div className="d-flex flex-wrap align-items-center filter-group">
            <Input
              type="text"
              placeholder="Search by assessment"
              className=" mr-2 mb-2 filter-input-large"
              value={searchState.assessmentName}
              onChange={(e) =>
                setSearchState((prev) => ({
                  ...prev,
                  assessmentName: e.target.value,
                }))
              }
              onKeyDown={handleKeyDown}
            />
            <Input
              type="text"
              placeholder="Search by batch"
              className=" mr-2 mb-2 filter-input-large"
              value={searchState.batchName}
              onChange={(e) =>
                setSearchState((prev) => ({
                  ...prev,
                  batchName: e.target.value,
                }))
              }
              onKeyDown={handleKeyDown}
            />
            <Select
              className="mr-2 mb-2 filter-select"
              options={courseOptions}
              placeholder="Select course name"
              classNamePrefix="react-select"
              styles={fixedWidthCustomStyle()}
              value={searchState.courseName}
              onChange={(selected) =>
                setSearchState((prev) => ({
                  ...prev,
                  courseName: selected,
                }))
              }
            />
            <Button
              color="default"
              className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${
                alternative.showLoaderOnClick ? "search-btn" : ""
              }`}
              onClick={handleSearch}
            >
              {alternative.showLoaderOnClick ? (
                <i className="fa fa-spinner fa-spin loader" />
              ) : (
                <>
                  <i className="fa fa-search mr-1" />
                  Search
                </>
              )}
            </Button>
            {searchState.filterParams?.filters?.length > 0 && (
              <Button
                color="default"
                className="d-flex align-items-start mb-2 ml-0 filter-btn btn-deep-blue px-4"
                onClick={clearSearch}
              >
                <i className="fa fa-times mr-1 mt-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
        <div className="mt-2 mb-3 mx-1">
          {traineeAssessmentDataList?.data?.totalRecords ? (
            <>
              {searchState.filterParams?.filters?.length > 0 ? (
                <span className="total-records">
                  Showing{" "}
                  {traineeAssessmentDataList?.data?.totalFilteredRecords}
                  {"  "}
                  of {traineeAssessmentDataList?.data?.totalRecords} Records
                </span>
              ) : (
                <span className="total-records">
                  Total Records :{" "}
                  {traineeAssessmentDataList?.data?.totalRecords}
                </span>
              )}
            </>
          ) : null}
        </div>
        {alternative.isLoading ? (
          <div className="loaderContainer-sm">
            <i className="fa fa-spinner fa-spin fa-2xl loader" />
          </div>
        ) : alternative.error ? (
          <div className="loaderContainer-sm error-style">
            <div className="error-icon">
              <i className="fa-solid fa-circle-exclamation"></i>
            </div>
            <span>{alternative.error}</span>
          </div>
        ) : alternative.noRecords ? (
          <div className="loaderContainer-sm empty-style">
            <div className="empty-icon">
              <i className="fa-solid fa-circle-info"></i>
            </div>
            <span>No assessments found.</span>
          </div>
        ) : (
          <div className="table-responsive">
            <CommonTable
              columns={columns}
              data={formattedTraineeAssessmentList || []}
              defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
              activePage={pageNum}
              setPageNum={setPageNum}
              totalPages={Math.ceil(
                (traineeAssessmentDataList?.data?.totalFilteredRecords || 0) /
                  (traineeAssessmentDataList?.data?.noOfRecordsPerPage ||
                    DEFAULT_RECORDS_PER_PAGE),
              )}
              showPaginationBottom={true}
            />
          </div>
        )}
        <CommonModal
          isOpen={showAssessmentModal.isOpen}
          toggle={handleCloseModal}
          title="Artifacts"
          size="xl"
        >
          <Row className="artifact-container">
            <Col xs="12" md="2" className="artifact-left">
              <div className="artifact-list">
                {artifacts.map((artifact) => {
                  const fileUrl = artifact.filePath || "";

                  const extension = fileUrl
                    .split("?")[0]
                    .split("#")[0]
                    .split(".")
                    .pop()
                    ?.toLowerCase();

                  const isImage = [
                    "jpg",
                    "jpeg",
                    "png",
                    "gif",
                    "webp",
                  ].includes(extension);

                  // const isVideo = ["mp4", "webm", "ogg"].includes(extension);
                  return (
                    <div
                      key={artifact.id}
                      className={`artifact-document-card ${
                        selectedArtifact?.id === artifact.id
                          ? "selected-artifact-document"
                          : ""
                      }`}
                      onClick={() => {
                        setPreviewLoading(true);
                        setSelectedArtifact(artifact);
                      }}
                    >
                      <div className="artifact-document-thumbnail">
                        {isImage ? (
                          <img
                            src={fileUrl}
                            alt="Artifact"
                            className="thumbnail-image"
                          />
                        ) : artifact.fileType === "video" ? (
                          <video
                            className="thumbnail-video"
                            muted
                            preload="metadata"
                          >
                            <source src={fileUrl} />
                          </video>
                        ) : (
                          <div className="file-thumbnail">
                            <i
                              className={`fas lessonIcon ${
                                extension === "pdf"
                                  ? "fa-file-pdf"
                                  : ["doc", "docx"].includes(extension)
                                    ? "fa-file-word"
                                    : ["xls", "xlsx"].includes(extension)
                                      ? "fa-file-excel"
                                      : ["ppt", "pptx"].includes(extension)
                                        ? "fa-file-powerpoint"
                                        : "fa-file"
                              }`}
                            />
                          </div>
                        )}
                      </div>
                      <div className="artifact-document-divider" />
                      <div className="artifact-document-info">
                        <div className="artifact-document-date">
                          {artifact.uploadedOn}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Col>
            <Col xs="12" md="10" className="artifact-right">
              <div className="artifact-preview">
                {previewLoading && (
                  <div className="preview-loader">
                    <i className="fa fa-spinner fa-spin loader fa-2x text-default" />
                  </div>
                )}
                {!previewLoading && artifacts.length === 0 ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info"></i>
                    </div>
                    <span>No artifacts found</span>
                  </div>
                ) : (
                  renderPreview(selectedArtifact)
                )}
              </div>
            </Col>
          </Row>
        </CommonModal>
      </CardBody>
    </Card>
  );
}
export default TraineeAssessment;
